import React, { useMemo, useCallback } from 'react';
import PieChart from './PieChart';
import './Fire.scss';
import calendar from '../../../assets/icons/calendar-icon.png';
import alertsIcon from '../../../assets/icons/bell-icon.png';
import location from '../../../assets/icons/location-icon.png';
import moment from 'moment-timezone';
import LiveTime from './LiveTime';
import * as turf from '@turf/turf';
import { useAlertsContext } from '../../../contexts/AlertsContext';
import { useUserContext } from '../../../contexts/UserContext';
import optionsIcon from '../../../assets/icons/options-icon.png';
import warningIcon from '../../../assets/icons/hazard-icon.png';
import usaFlagIcon from '../../../assets/icons/usa-flag.png';
import canadaFlagIcon from '../../../assets/icons/canada-flag.png';
import fireContainedUrl from '../../../assets/icons/fire-gray.png';
import fireHalfContainedUrl from '../../../assets/icons/fire-half-gray.png';
import fireCircleUrl from '../../../assets/icons/fire-red-orange.png';
import fireAlmostGray from '../../../assets/icons/fire-almost-gray.png';
import historicalFireIcon from '../../../assets/icons/historical-fire-icon.png';
import { BellSimpleRingingIcon } from '@phosphor-icons/react';

export default function HistoricalFire(props) {
    const {
        is3D,
        setIs3D
    } = useUserContext();

    const {
        alerts
    } = useAlertsContext();

    const { 
        setPanel,
        fire,
        user,
        map,
        setModal
    } = props;

    const userSub = user?.accessToken.payload.sub;

    const returnFireIcon = useCallback((perContained) => {
        const pc = perContained ?? 0;
      
        if (pc < 41)          return fireCircleUrl;            // 0–40
        if (pc < 91)          return fireHalfContainedUrl;     // 41–90
        if (pc < 100)         return fireAlmostGray;           // 91–99
        /* pc === 100 */      return fireContainedUrl;         // 100
    }, []);

    const timeDifference = (startTimestamp, endTimestamp) => {
        const start = new Date(startTimestamp);
        const end = new Date(endTimestamp);

        const diffMs = Math.abs(end - start); // Difference in milliseconds

        const diffSeconds = Math.floor(diffMs / 1000) % 60;
        const diffMinutes = Math.floor(diffMs / (1000 * 60)) % 60;
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60)) % 24;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        const parts = [];
        if (diffDays > 0) parts.push(`${diffDays} day${diffDays > 1 ? 's' : ''}`);
        if (diffHours > 0) parts.push(`${diffHours} hour${diffHours > 1 ? 's' : ''}`);
        if (diffMinutes > 0) parts.push(`${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`);
        if (diffSeconds > 0) parts.push(`${diffSeconds} second${diffSeconds > 1 ? 's' : ''}`);

        return parts.length > 0 ? parts.join(', ') : '0 seconds';
    }

    const returnFlag = (country) => {
        switch (country) {
            case 'United States':
                return usaFlagIcon
            case 'Canada':
                return canadaFlagIcon
        }
    }

    const fireContainmentChange = (oldPercentage, newPercentage) => {
        const difference = newPercentage - oldPercentage;
      
        const changeType = difference > 0 ? "increased" : "decreased";
        const absoluteDifference = Math.abs(difference);
      
        return `${changeType} by ${absoluteDifference}%`;
    }

    const WKTtoRings = (wkt) => {
        const rings = wkt.slice(9).slice(0, -2).split(',').map((coordinates) => {
            return coordinates.split(' ').filter((element) => element !== '').map((element) => parseFloat(element));
        });
        return rings;
    };

    const formatDate = useCallback((timestamp) => {
        const time = moment.utc(timestamp).local();
        return time.format('YYYY-MM-DD hh:mm:ss A');
    }, []);

    const getUploaderText = useMemo(() => {
        const { fire_id, cognito_user_id } = fire;
        const userSub = user?.accessToken.payload.sub;
    
        if (!fire_id.toUpperCase().includes('USER-')) {
            return `Source: ${fire.data_source ? fire.data_source.replaceAll('(x-weather)', '') : 'Not Available'}`;
        }
    
        if (cognito_user_id === userSub) {
            return 'Uploaded By You';
        }
    
        return 'Uploaded By Community User';
    }, [fire.fire_id, fire.cognito_user_id, fire.data_source, user?.accessToken.payload.sub]);

    const goToFire = useCallback(() => {
        setPanel({
            name: 'Historical Fire PopUp',
            content: fire
        })

        const rings = fire.geom ? WKTtoRings(fire.geom) : '';

        const geometry = {
            'type': 'Feature',
            'geometry': {
              'type': 'Polygon',
              'coordinates': [rings]
            }
        }

        const location = [fire.longitude, fire.latitude];

        if (!fire.geom) {
            map.current.flyTo({ center: location, pitch: 0, zoom: 17 });
        } else {
            map.current.fitBounds(turf.bbox(geometry), {
                padding: 200,
                pitch: 0
            });
        }

        if (is3D) {
            setIs3D(false);
        }
    }, [setPanel, fire, map, is3D, setIs3D]);

    const fireIcon = useMemo(() => returnFireIcon(fire.perContained), [returnFireIcon, fire.perContained]);
    
    const hasAlerts = useMemo(() => 
        alerts.filter((a) => a.incident_id === fire.fire_id).length > 0,
        [alerts, fire.fire_id]
    );

    return (
        <div className='fire-card'>
            {userSub === fire.cognito_user_id &&
            <img 
                src={optionsIcon}
                className='fire-card-options'
                onClick={() => {
                    setModal({
                        type: 'edit-and-delete',
                        content: fire
                    })
                }}
            />}
            <div className='fire-card-header'>
                <div className='fire-card-header-title'><img src={historicalFireIcon} style={{ width: '1.5rem' }}/>{fire.name ? fire.name : 'Not Available'}</div>
                <div className='fire-card-header-datasource'>
                    {getUploaderText} 
                    {fire.data_source !== 'Premium Users' && <img src={returnFlag((fire.data_source && (fire.data_source.includes('CWFIS') || fire.data_source.includes('CIFFC')) ? 'Canada' : 'United States'))} style={{ width: 25, height: 25 }}/>}
                </div>
            </div>
            <div className="fire-card-main">
                <div className="fire-card-main-left">
                    <div className="fire-card-main-left-subheader">
                        <div className="fire-card-main-left-subheader-status">
                            <div className="fire-card-main-left-subheader-status-title">Status</div>
                            <div
                                className="fire-card-main-left-subheader-status-value"
                                style={{
                                    color: fire.active ? '#f44336' : '#37c13e',
                                }}
                            >
                                {fire.active ? 'Active' : 'Contained'}
                            </div>
                        </div>
                        {fire.updatedTimestamp && (
                            <div className="fire-card-main-left-subheader-lastupdated">
                                <div className="fire-card-main-left-subheader-lastupdated-title">Last Updated</div>
                                <LiveTime value={fire.updatedTimestamp}/>
                            </div>
                        )}
                    </div>
                    <div className="fire-card-main-left-date">
                        <img src={calendar} className="fire-card-main-left-date-icon" alt="Calendar" />
                        <div className="fire-card-main-left-date-value">
                            {fire.startTimestamp
                                ? formatDate(fire.startTimestamp)
                                : fire.starttimestamp
                                ? formatDate(fire.starttimestamp)
                                : 'Not Available'
                            }
                        </div>
                    </div>
                    <div className="fire-card-main-left-location">
                        <img src={location} className="fire-card-main-left-location-icon" alt="Location" />
                        <div className="fire-card-main-left-location-value">
                            {`${fire.latitude.toFixed(5)}, ${fire.longitude.toFixed(5)}`}
                        </div>
                    </div>
                </div>
                <div className="fire-card-main-right">
                    <PieChart 
                        percentage={fire.perContained}
                        size={80}
                        type='containment'
                    />
                    <div className="fire-card-main-right-caption">Containment</div>
                </div>
            </div>
            {((fire.perContained || fire.perContained === 0) && fire.last_containment && (fire.last_containment.percontained || fire.last_containment.percontained === 0) && fire.last_containment.timestamp) && (fire.perContained - fire.last_containment.percontained !== 0) &&
                        <div className='fire-card-main-left-containment-update'>
                            <img src={warningIcon} className='fire-card-main-left-containment-update-icon'/>
                            <div className='fire-card-main-left-containment-update-text'>
                                {`Fire containment has `}
                                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}>{fireContainmentChange(fire.last_containment.percontained, fire.perContained)}</span>
                                <span 
                                    style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'black' }}
                                >
                                    {` in the span of ${timeDifference(fire.last_containment.timestamp, fire.updatedTimestamp ? fire.updatedTimestamp : fire.startTimestamp)}.`}
                                </span>
                            </div>
                        </div>}
            <div className="fire-card-btns">
                <div 
                    className="fire-card-btns-btn-view"
                    onClick={goToFire}    
                    >
                    View
                </div>
                {hasAlerts ?
                <div 
                    className='fire-card-btns-btn-alerts-on'
                    onClick={() => {
                        setModal({
                        type: 'alerts',
                        content: {
                            setModal: setModal,
                            fireId: fire.fire_id
                        }
                        })
                    }}
                    >
                    <img 
                        src={alertsIcon} 
                        className='fire-card-btns-btn-alerts-on-icon'
                    />
                    Alerts On
                    </div>
                    :
                    <div 
                    className='fire-card-btns-btn-alerts-off'
                    onClick={() => {
                        setModal({
                            type: 'alerts',
                            content: {
                                setModal: setModal,
                                fireId: fire.fire_id
                            }
                        })
                    }}
                >
                   <BellSimpleRingingIcon size={16} weight='fill'/>
                    Turn On Alerts
                </div>}
            </div>
        </div>
    );
}